import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import { adminApi } from '../../services/admin';
import ConditionBuilder from '../../components/ConditionBuilder';
import extractApiError from '../../utils/extractApiError';

const CATEGORIES = ['attendance', 'patrol', 'incident', 'report', 'feedback', 'operational'];
const TARGET_SCORES = ['guard', 'site', 'vendor'];
const RULE_TYPES = ['threshold', 'frequency', 'streak', 'trend', 'composite', 'absence', 'cross_entity'];
const ENTITIES = ['guard', 'site', 'vendor'];
const SEVERITIES = ['low', 'medium', 'high', 'critical'];

function isStandardRuleType(type: string) {
  return ['threshold', 'frequency', 'absence', 'composite'].includes(type);
}

interface FormData {
  name: string;
  description: string;
  category: string;
  target_score: string;
  rule_type: string;
  evaluation_entity: string;
  metric_source: string;
  conditions: string;
  time_window_days: number;
  score_impact: number;
  is_positive: boolean;
  severity_level: string;
  is_active: boolean;
  group: string;
  priority: number;
  max_triggers_per_cycle: string;
  cooldown_weeks: number;
  explanation_template: string;
}

const defaultForm: FormData = {
  name: '',
  description: '',
  category: 'attendance',
  target_score: 'guard',
  rule_type: 'threshold',
  evaluation_entity: 'guard',
  metric_source: 'daily_shift_summary',
  conditions: '{}',
  time_window_days: 7,
  score_impact: 5,
  is_positive: false,
  severity_level: 'medium',
  is_active: false,
  group: '',
  priority: 0,
  max_triggers_per_cycle: '',
  cooldown_weeks: 0,
  explanation_template: '',
};

export default function AdminRuleForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormData>(defaultForm);
  const [error, setError] = useState<string | null>(null);

  const { data: rule, isLoading } = useQuery({
    queryKey: ['admin', 'rules', id],
    queryFn: () => adminApi.getRule(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (rule) {
      setForm({
        name: rule.name,
        description: rule.description,
        category: rule.category,
        target_score: rule.target_score,
        rule_type: rule.rule_type,
        evaluation_entity: rule.evaluation_entity,
        metric_source: rule.metric_source,
        conditions: JSON.stringify(rule.conditions, null, 2),
        time_window_days: rule.time_window_days,
        score_impact: Number(rule.score_impact),
        is_positive: rule.is_positive,
        severity_level: rule.severity_level,
        is_active: rule.is_active,
        group: rule.group || '',
        priority: rule.priority,
        max_triggers_per_cycle: rule.max_triggers_per_cycle != null ? String(rule.max_triggers_per_cycle) : '',
        cooldown_weeks: rule.cooldown_weeks,
        explanation_template: rule.explanation_template,
      });
    }
  }, [rule]);

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      isEdit ? adminApi.updateRule(id!, data) : adminApi.createRule(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'rules'] });
      navigate(`/admin/rules/${result.id}`);
    },
    onError: (err: unknown) => {
      setError(extractApiError(err));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    let parsedConditions: Record<string, unknown>;
    try {
      parsedConditions = JSON.parse(form.conditions);
    } catch {
      setError('Conditions must be valid JSON');
      return;
    }

    mutation.mutate({
      name: form.name,
      description: form.description,
      category: form.category,
      target_score: form.target_score,
      rule_type: form.rule_type,
      evaluation_entity: form.evaluation_entity,
      metric_source: form.metric_source,
      conditions: parsedConditions,
      time_window_days: form.time_window_days,
      score_impact: form.score_impact,
      is_positive: form.is_positive,
      severity_level: form.severity_level,
      is_active: form.is_active,
      group: form.group || null,
      priority: form.priority,
      max_triggers_per_cycle: form.max_triggers_per_cycle ? Number(form.max_triggers_per_cycle) : null,
      cooldown_weeks: form.cooldown_weeks,
      explanation_template: form.explanation_template,
    });
  };

  const set = <K extends keyof FormData>(key: K, value: FormData[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleConditionsChange = useCallback((json: string) => {
    setForm(prev => ({ ...prev, conditions: json }));
  }, []);

  if (isEdit && isLoading) return <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>;

  return (
    <>
      <PageHeader
        title={isEdit ? `Edit: ${rule?.name}` : 'Create Rule'}
        description={isEdit ? `Editing v${rule?.version}` : 'Define a new scoring rule'}
      />
      <form onSubmit={handleSubmit} className="max-w-4xl space-y-6">
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Section 1: Basic Info */}
        <div className="rounded-2xl border border-slate-100 bg-white p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Basic Info</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate-500 mb-1">Name</label>
              <input type="text" required value={form.name} onChange={e => set('name', e.target.value)}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate-500 mb-1">Description</label>
              <textarea required value={form.description} onChange={e => set('description', e.target.value)} rows={2}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Severity</label>
              <select value={form.severity_level} onChange={e => set('severity_level', e.target.value)}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none">
                {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Group</label>
              <input type="text" value={form.group} onChange={e => set('group', e.target.value)}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
          </div>
        </div>

        {/* Section 2: Target & Type */}
        <div className="rounded-2xl border border-slate-100 bg-white p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Target & Scope</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Target Score</label>
              <select value={form.target_score} onChange={e => set('target_score', e.target.value)}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none">
                {TARGET_SCORES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Rule Type</label>
              <select value={form.rule_type} onChange={e => set('rule_type', e.target.value)}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none">
                {RULE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Evaluation Entity</label>
              <select value={form.evaluation_entity} onChange={e => set('evaluation_entity', e.target.value)}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none">
                {ENTITIES.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Metric Source</label>
              <select value={form.metric_source} onChange={e => set('metric_source', e.target.value)}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none">
                <option value="daily_shift_summary">Daily Shift Summary</option>
                <option value="parsed_event">Parsed Event</option>
                <option value="upload">Upload</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Time Window (days)</label>
              <input type="number" min={1} value={form.time_window_days} onChange={e => set('time_window_days', Number(e.target.value))}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
          </div>
        </div>

        {/* Section 3: Conditions */}
        <div className="rounded-2xl border border-slate-100 bg-white p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Conditions</h3>
          <ConditionBuilder
            value={form.conditions}
            onChange={handleConditionsChange}
            ruleType={form.rule_type}
          />
        </div>

        {/* Section 4: Impact */}
        <div className="rounded-2xl border border-slate-100 bg-white p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Score Impact</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Impact Points</label>
              <input type="number" step="0.01" value={form.score_impact} onChange={e => set('score_impact', Number(e.target.value))}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={form.is_positive} onChange={e => set('is_positive', e.target.checked)}
                  className="rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                Reward (positive impact)
              </label>
            </div>
          </div>
        </div>

        {/* Section 5: Advanced */}
        <div className="rounded-2xl border border-slate-100 bg-white p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Advanced Settings</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Priority</label>
              <input type="number" value={form.priority} onChange={e => set('priority', Number(e.target.value))}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Max Triggers/Cycle</label>
              <input type="number" min={1} value={form.max_triggers_per_cycle} onChange={e => set('max_triggers_per_cycle', e.target.value)} placeholder="Unlimited"
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Cooldown (weeks)</label>
              <input type="number" min={0} value={form.cooldown_weeks} onChange={e => set('cooldown_weeks', Number(e.target.value))}
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)}
                  className="rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                Active
              </label>
            </div>
          </div>
        </div>

        {/* Section 6: Explanation */}
        <div className="rounded-2xl border border-slate-100 bg-white p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Explanation Template</h3>
          <textarea value={form.explanation_template} onChange={e => set('explanation_template', e.target.value)} rows={3}
            className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            placeholder="e.g., Guard had {dar_submitted} missed DARs this week (threshold: 2)." />

          {/* Variable Cheatsheet */}
          <details className="mt-3">
            <summary className="text-xs text-slate-400 hover:text-slate-600 cursor-pointer select-none">
              Available variables cheatsheet
            </summary>
            <div className="mt-2 rounded-lg bg-slate-50 border border-slate-100 p-3 text-xs text-slate-600 space-y-3">
              <p className="text-slate-400">
                Use <code className="bg-slate-200 px-1 rounded">{'{variable_name}'}</code> in your template. The engine replaces them with real values when the rule triggers.
              </p>

              {(isStandardRuleType(form.rule_type)) && (
                <div>
                  <p className="font-medium text-slate-700 mb-1">Standard / Frequency / Absence / Composite Rules</p>
                  <p className="text-slate-400 mb-1">The variable name matches the field you chose in the condition:</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono text-slate-500">
                    <span>{'{dar_submitted}'}</span><span>DAR count value</span>
                    <span>{'{total_patrols}'}</span><span>Patrol count value</span>
                    <span>{'{total_incidents}'}</span><span>Incident count value</span>
                    <span>{'{photo_count}'}</span><span>Photo count value</span>
                    <span>{'{notes_quality_score}'}</span><span>Notes quality value</span>
                    <span>{'{max_patrol_gap_minutes}'}</span><span>Max gap value</span>
                  </div>
                  <p className="text-slate-400 mt-1">Any field from your condition check is available as a variable.</p>
                </div>
              )}

              {form.rule_type === 'trend' && (
                <div>
                  <p className="font-medium text-slate-700 mb-1">Trend Rules</p>
                  <p className="text-slate-400 mb-1">For a field like <code className="bg-slate-200 px-1 rounded">total_incidents</code>:</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono text-slate-500">
                    <span>{'{total_incidents}'}</span><span>Current period value</span>
                    <span>{'{total_incidents_previous}'}</span><span>Prior period value</span>
                    <span>{'{total_incidents_pct_change}'}</span><span>% change (e.g. 52.3)</span>
                  </div>
                  <p className="text-slate-400 mt-1">Replace <code className="bg-slate-200 px-1 rounded">total_incidents</code> with whatever field you chose.</p>
                </div>
              )}

              {form.rule_type === 'streak' && (
                <div>
                  <p className="font-medium text-slate-700 mb-1">Streak Rules</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono text-slate-500">
                    <span>{'{streak_weeks_passed}'}</span><span>Weeks that passed the check</span>
                    <span>{'{streak_weeks_required}'}</span><span>Weeks required</span>
                    <span>{'{streak_complete}'}</span><span>True / False</span>
                  </div>
                  <p className="text-slate-400 mt-1">Plus any field variable from the condition check.</p>
                </div>
              )}

              {form.rule_type === 'cross_entity' && (
                <div>
                  <p className="font-medium text-slate-700 mb-1">Cross-Entity Rules</p>
                  <p className="text-slate-400 mb-1">Score-based (Guard/Site Scores):</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono text-slate-500">
                    <span>{'{guard_score_avg}'}</span><span>Avg guard score</span>
                    <span>{'{guard_score_min}'}</span><span>Min guard score</span>
                    <span>{'{guard_score_max}'}</span><span>Max guard score</span>
                    <span>{'{site_score_avg}'}</span><span>Avg site score</span>
                    <span>{'{site_score_min}'}</span><span>Min site score</span>
                  </div>
                  <p className="text-slate-400 mt-2 mb-1">Summary-based (Daily Shift Summaries):</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono text-slate-500">
                    <span>{'{matching_entities}'}</span><span>Entities that matched</span>
                    <span>{'{total_entities}'}</span><span>Total entities checked</span>
                  </div>
                </div>
              )}
            </div>
          </details>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button type="submit" disabled={mutation.isPending}
            className="px-6 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50">
            {mutation.isPending ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Rule'}
          </button>
          <button type="button" onClick={() => navigate('/admin/rules')}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50">
            Cancel
          </button>
        </div>
      </form>
    </>
  );
}
